import { applicationPath } from '@vivaporta/work-areas';
import { careWorkAreaCopy, type WorkAreaLocale } from '@vivaporta/work-areas';
import { careIndustryAreas, type CareIndustryPageKey } from './careIndustryLabels.js';
import { resolveApplicationPortalBase } from './landingContentShared.js';
import type { StaticPageAudience, StaticPageContent } from './staticPageContent.js';
import care from '../../img/industries/care-portrait.webp';
import team from '../../img/industries/care-team.webp';

const headlines: Record<WorkAreaLocale, Record<CareIndustryPageKey, readonly [string, string, string, string]>> = {
  de: {
    pflegefachkraft: ['Pflege mit Zukunft.', 'Zukunft', 'Ihr Pflegeteam braucht Menschen.', 'Menschen'],
    'op-pflege-ota': ['OP & OTA. Dein neuer Start.', 'Start', 'Ihr OP-Team braucht Menschen.', 'Menschen'],
    'radiologie-mtra': ['Radiologie mit Zukunft.', 'Zukunft', 'Ihre Radiologie braucht Können.', 'Können'],
    'medizinische-fachangestellte': ['MFA. Dein Platz im Team.', 'Team', 'Ihre Praxis braucht Menschen.', 'Menschen'],
    physiotherapie: ['Physiotherapie mit Zukunft.', 'Zukunft', 'Ihre Therapie braucht Menschen.', 'Menschen'],
    'labor-gesundheit': ['Labor. Präzision mit Zukunft.', 'Präzision', 'Ihr Labor braucht Präzision.', 'Präzision'],
  },
  en: {
    pflegefachkraft: ['Nursing with a future.', 'future', 'Your care team needs people.', 'people'],
    'op-pflege-ota': ['Surgery. Your next step.', 'step', 'Your surgical team needs people.', 'people'],
    'radiologie-mtra': ['Radiology with a future.', 'future', 'Your radiology team needs skills.', 'skills'],
    'medizinische-fachangestellte': ['MFA. Your place in the team.', 'team', 'Your practice needs people.', 'people'],
    physiotherapie: ['Physiotherapy with a future.', 'future', 'Your therapy team needs people.', 'people'],
    'labor-gesundheit': ['Laboratory. Skills for tomorrow.', 'Skills', 'Your laboratory needs precision.', 'precision'],
  },
  tr: {
    pflegefachkraft: ['Hemşirelikte geleceğin.', 'geleceğin', 'Bakım ekibiniz güçlensin.', 'güçlensin'],
    'op-pflege-ota': ['Ameliyathanede yeni başlangıç.', 'başlangıç', 'Ameliyat ekibiniz güçlensin.', 'güçlensin'],
    'radiologie-mtra': ['Radyolojide geleceğin.', 'geleceğin', 'Radyoloji ekibiniz güçlensin.', 'güçlensin'],
    'medizinische-fachangestellte': ['MFA. Ekipteki yerin.', 'yerin', 'Muayenehane ekibiniz güçlensin.', 'güçlensin'],
    physiotherapie: ['Fizyoterapide geleceğin.', 'geleceğin', 'Terapi ekibiniz güçlensin.', 'güçlensin'],
    'labor-gesundheit': ['Laboratuvarda geleceğin.', 'geleceğin', 'Laboratuvar ekibiniz güçlensin.', 'güçlensin'],
  },
};
const ui = {
  de: {
    apply: 'Hier anmelden',
    intro: 'Wir begleiten dich bei Qualifikation, Sprache und dem passenden Einstieg in Deutschland.',
    company: 'Wir begleiten internationale Fachkräfte auf dem Weg in Ihr Team.',
    overview: 'Deine Erfahrung zählt.',
    prepare: 'Das bringst du mit.',
    prepareText:
      'Erzähle uns von deiner Ausbildung, deinen bisherigen Aufgaben und deinem Sprachstand. Nachweise und Angaben zur Anerkennung helfen uns, dein Profil einzuordnen und die nächsten Schritte persönlich mit dir zu besprechen.',
    caption: ['Menschen im Gesundheitswesen', 'Gemeinsam im Team'],
    alt: ['Porträt einer Fachkraft im Gesundheitswesen.', 'Fachkraft in einer Gesundheitseinrichtung.'],
    cta: 'Mach den nächsten Schritt.',
    text: 'Starte mit dem passenden Bewerbungsbogen. Wir lernen dich und deine berufliche Erfahrung kennen.',
    process: 'So begleiten wir dich',
  },
  en: {
    apply: 'Apply here',
    intro: 'We guide you through qualifications, language preparation and a suitable start in Germany.',
    company: 'We support international professionals on their way into your team.',
    overview: 'Your experience matters.',
    prepare: 'Tell us about your background.',
    prepareText:
      'Tell us about your training, previous responsibilities and language skills. Documents and recognition details help us understand your profile and discuss the next steps with you personally.',
    caption: ['People in healthcare', 'Working together'],
    alt: ['Portrait of a healthcare professional.', 'Professional in a healthcare setting.'],
    cta: 'Take your next step.',
    text: 'Start with the matching application form. We look forward to learning about your experience.',
    process: 'How we support you',
  },
  tr: {
    apply: 'Buradan başvur',
    intro: 'Yeterlilik, dil hazırlığı ve Almanya’da uygun bir başlangıç için sana eşlik ediyoruz.',
    company: 'Uluslararası uzmanlara ekibinize katılma yolunda eşlik ediyoruz.',
    overview: 'Deneyimin değerli.',
    prepare: 'Bize geçmişini anlat.',
    prepareText:
      'Eğitimini, önceki görevlerini ve dil seviyeni paylaş. Belgelerin ve denklik bilgilerin, profilini değerlendirmemize ve sonraki adımları seninle görüşmemize yardımcı olur.',
    caption: ['Sağlık alanındaki insanlar', 'Birlikte çalışmak'],
    alt: ['Bir sağlık çalışanının portresi.', 'Bir sağlık kuruluşunda çalışan uzman.'],
    cta: 'Bir sonraki adımı at.',
    text: 'Uygun başvuru formuyla başla. Seni ve mesleki deneyimini tanımak istiyoruz.',
    process: 'Sana nasıl eşlik ediyoruz',
  },
} as const;
export const careIndustryHeroCopy = (key: CareIndustryPageKey, locale: WorkAreaLocale, audience: StaticPageAudience) => {
  const h = headlines[locale][key],
    c = careWorkAreaCopy(key, locale),
    t = ui[locale];
  return {
    title: h[audience === 'company' ? 2 : 0],
    heroTitleHighlight: h[audience === 'company' ? 3 : 1],
    intro: `${c.description} ${audience === 'company' ? t.company : t.intro}`,
  };
};
export const createCareIndustryPages = (locale: WorkAreaLocale = 'de'): Record<CareIndustryPageKey, StaticPageContent> =>
  Object.fromEntries(
    careIndustryAreas.map((area, index) => {
      const c = careWorkAreaCopy(area.key, locale),
        t = ui[locale],
        images = index % 2 ? [team, care] : [care, team];
      const action = { label: t.apply, href: `${resolveApplicationPortalBase()}${applicationPath(area.key, locale)}?lang=${locale}` };
      return [
        area.key,
        {
          key: area.key,
          eyebrow: c.label,
          ...careIndustryHeroCopy(area.key, locale, 'applicant'),
          heroBackgroundWord: c.tags[0],
          heroImage: images[0],
          heroGallery: images.map((image, i) => ({ image, imageAlt: t.alt[i], title: t.caption[i] })),
          heroAction: action,
          heroHighlights: [...c.tags],
          cardsTitle: t.overview,
          cards: c.tags.map(title => ({ title, text: c.description, iconClass: area.icon })),
          sections: [{ eyebrow: c.label, title: t.prepare, text: t.prepareText, image: images[1], imageAlt: t.alt[1] }],
          cta: {
            eyebrow: c.label,
            title: t.cta,
            text: t.text,
            primaryAction: action,
            secondaryAction: { label: t.process, href: '/ablauf' },
          },
        },
      ];
    }),
  ) as Record<CareIndustryPageKey, StaticPageContent>;
